// Print-ready exports of label and packaging designs (Stage 2.6B): SVG with its images inside, and PDF at the exact
// label size (one side per page), rasterised at up to 600 dpi and stored losslessly (Flate), so barcodes stay sharp.
import { needsEmbeddedFont } from "../../shared/languages.js";

/** Put every image the SVG uses inside it (data URLs), so the file stands alone and can be drawn to a canvas. */
export async function inlineImages(svg: string): Promise<string> {
  const urls = [...new Set([...svg.matchAll(/href="([^"#][^"]*)"/g)].map((m) => m[1]).filter((u) => !u.startsWith("data:")))];
  const map = new Map<string, string>();
  for (const u of urls) {
    try {
      const res = await fetch(u.replace(/&amp;/g, "&"), { credentials: "same-origin" });
      if (!res.ok) continue;
      const blob = await res.blob();
      map.set(u, await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = reject; r.readAsDataURL(blob); }));
    } catch { /* leave it out */ }
  }
  return embedFonts(svg.replace(/href="([^"#][^"]*)"/g, (m, u) => (map.has(u) ? `href="${map.get(u)}"` : u.startsWith("data:") ? m : 'href=""')));
}

/** The label fonts, loaded once (Noto Sans Arabic / Noto Naskh Arabic Regular and Bold, Noto Sans Latin). */
const FONT_FILES: Array<[string, number, string]> = [
  ["Noto Naskh Arabic", 400, "noto-naskh-arabic-arabic-400-normal"], ["Noto Naskh Arabic", 700, "noto-naskh-arabic-arabic-700-normal"],
  ["Noto Sans Arabic", 400, "noto-sans-arabic-arabic-400-normal"], ["Noto Sans Arabic", 700, "noto-sans-arabic-arabic-700-normal"],
  ["Noto Sans", 400, "noto-sans-latin-400-normal"], ["Noto Sans", 700, "noto-sans-latin-700-normal"],
];
let fontCss: Promise<string> | null = null;
function loadFontCss(): Promise<string> {
  fontCss ||= Promise.all(FONT_FILES.map(async ([family, weight, file]) => {
    try {
      const blob = await (await fetch(`/fonts/${file}.woff2`)).blob();
      const url = await new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result).replace(/^data:[^;,]*/, "data:font/woff2")); r.onerror = no; r.readAsDataURL(blob); });
      return `@font-face{font-family:"${family}";font-weight:${weight};font-style:normal;src:url(${url}) format("woff2")}`;
    } catch { return ""; }
  })).then((x) => x.join(""));
  return fontCss;
}
/** Embed the fonts in an SVG that prints anything beyond Latin (Arabic, Urdu, …), so it prints with joined letters and
 *  right-to-left order on any computer, in any program (and when drawn to a PDF page). */
export async function embedFonts(svg: string | Promise<string>): Promise<string> {
  const s = await svg;
  const text = s.replace(/<[^>]*>/g, " ");
  if (!needsEmbeddedFont(text) || s.includes("@font-face")) return s;
  const css = await loadFontCss();
  return css ? s.replace(/(<svg\b[^>]*>)/, `$1<defs><style>${css}</style></defs>`) : s;
}

/** Draw an SVG (sized in mm) onto a canvas at the given resolution. */
export async function svgToCanvas(svg: string, widthMm: number, heightMm: number, dpi: number): Promise<HTMLCanvasElement> {
  const px = (mm: number) => Math.max(1, Math.round((mm / 25.4) * dpi));
  const canvas = document.createElement("canvas");
  canvas.width = px(widthMm);
  canvas.height = px(heightMm);
  // Drawn as a picture, the SVG can't use the page's fonts: Arabic and other scripts need them embedded.
  const sized = (await embedFonts(svg)).replace(/width="[\d.]+mm" height="[\d.]+mm"/, `width="${canvas.width}" height="${canvas.height}"`);
  const url = URL.createObjectURL(new Blob([sized], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(new Error("The design couldn't be drawn.")); img.src = url; });
    const g = canvas.getContext("2d")!;
    g.fillStyle = "#fff";
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(img, 0, 0, canvas.width, canvas.height);
  } finally { URL.revokeObjectURL(url); }
  return canvas;
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** A PDF with one page per canvas, each page exactly the given size in mm. */
export function canvasesToPdf(pages: Array<{ canvas: HTMLCanvasElement; widthMm: number; heightMm: number }>): Promise<Uint8Array> {
  return imagesPdf(pages.map((p, i) => ({ ...p, key: String(i) })));
}

/** A PDF whose pages show images at exactly the given size in mm. Pages with the same key share one stored image,
 *  so a label-printer file with thousands of copies of the same label stays small. Stored losslessly (Flate). */
export async function imagesPdf(pages: Array<{ key: string; canvas: HTMLCanvasElement; widthMm: number; heightMm: number }>): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (b: Uint8Array | string) => { const x = typeof b === "string" ? enc.encode(b) : b; parts.push(x); length += x.length; };
  const obj = (n: number, body: () => void) => { offsets[n] = length; push(`${n} 0 obj\n`); body(); push("\nendobj\n"); };
  const pt = (mm: number) => ((mm / 25.4) * 72).toFixed(2);
  // Unique images first (objects 3…), then each page and its contents.
  const keys = [...new Set(pages.map((p) => p.key))];
  const imageNo = new Map<string, number>(keys.map((k, i) => [k, 3 + i]));
  const firstPage = 3 + keys.length;
  push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
  obj(1, () => push("<< /Type /Catalog /Pages 2 0 R >>"));
  obj(2, () => push(`<< /Type /Pages /Kids [${pages.map((_, i) => `${firstPage + i * 2} 0 R`).join(" ")}] /Count ${pages.length} >>`));
  for (const k of keys) {
    const c = pages.find((p) => p.key === k)!.canvas;
    const { width, height } = c;
    const rgba = c.getContext("2d")!.getImageData(0, 0, width, height).data;
    const rgb = new Uint8Array(width * height * 3);
    for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) { rgb[j] = rgba[i]; rgb[j + 1] = rgba[i + 1]; rgb[j + 2] = rgba[i + 2]; }
    const data = await deflate(rgb);
    obj(imageNo.get(k)!, () => {
      push(`<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /Length ${data.length} >>\nstream\n`);
      push(data);
      push("\nendstream");
    });
  }
  pages.forEach((p, i) => {
    const pageNo = firstPage + i * 2, contentNo = pageNo + 1, img = imageNo.get(p.key)!;
    const W = pt(p.widthMm), H = pt(p.heightMm);
    obj(pageNo, () => push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /XObject << /Im${img} ${img} 0 R >> >> /Contents ${contentNo} 0 R >>`));
    const draw = `q ${W} 0 0 ${H} 0 0 cm /Im${img} Do Q`;
    obj(contentNo, () => push(`<< /Length ${draw.length} >>\nstream\n${draw}\nendstream`));
  });
  const xref = length;
  const count = firstPage + pages.length * 2;
  push(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n++) push(`${String(offsets[n]).padStart(10, "0")} 00000 n \n`);
  push(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  const out = new Uint8Array(length);
  let o = 0;
  for (const x of parts) { out.set(x, o); o += x.length; }
  return out;
}

/** Resolution for a page: 600 dpi for labels, less for big packaging (keeps the canvas under ~16 million pixels). */
export const dpiFor = (widthMm: number, heightMm: number) => Math.max(72, Math.min(600, Math.floor(Math.sqrt(16_000_000 / ((widthMm / 25.4) * (heightMm / 25.4))))));

export function download(name: string, data: Uint8Array | string, type: string): void {
  const url = URL.createObjectURL(new Blob([data as BlobPart], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name.replace(/[^\w.\- ]+/g, "_");
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
